import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ChevronLeft, ChevronRight, Download, AlertCircle, ArrowLeft, Loader2, BookOpen
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function FreeBookReader() {
  const { bookId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookInfo, setBookInfo] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Initialize
  useEffect(() => {
    if (!bookId) {
      setError("Invalid document");
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        // Start free reader session
        const sessionRes = await axios.post(`${API}/books/free/reader/start-session/${bookId}`);
        setBookInfo(sessionRes.data);
        setSessionToken(sessionRes.data.session_token);
        
        // Get page count
        const pageRes = await axios.get(`${API}/books/free/reader/page-count?session_token=${sessionRes.data.session_token}`);
        setPageCount(pageRes.data.page_count);
        
        setLoading(false);
      } catch (err) {
        console.error("Init error:", err);
        setError(err.response?.data?.detail || "Failed to load document. This may not be a free document.");
        setLoading(false);
      }
    };

    init();
  }, [bookId]);

  // Preload adjacent pages for faster navigation
  useEffect(() => {
    if (!sessionToken || !pageCount) return;
    
    const pagesToPreload = [];
    if (currentPage > 1) pagesToPreload.push(currentPage - 1);
    if (currentPage < pageCount) pagesToPreload.push(currentPage + 1);
    if (currentPage < pageCount - 1) pagesToPreload.push(currentPage + 2);
    
    pagesToPreload.forEach(pageNum => {
      const img = new Image();
      img.src = `${API}/books/free/reader/page/${pageNum}?session_token=${sessionToken}`;
    });
  }, [sessionToken, currentPage, pageCount]);

  const goToPage = (page) => {
    if (page >= 1 && page <= pageCount) {
      setImageLoading(true);
      setImageError(false);
      setCurrentPage(page);
    }
  };

  const handleDownload = () => {
    window.open(`${API}/books/free/download/${bookId}`, '_blank');
  };

  // Get image URL for current page
  const imageUrl = sessionToken ? `${API}/books/free/reader/page/${currentPage}?session_token=${sessionToken}` : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-white font-dm-sans">Loading free document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111] border border-zinc-800 p-8 text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-outfit font-bold text-white mb-2">Cannot Access Document</h2>
          <p className="text-zinc-400 font-dm-sans mb-6">{error}</p>
          <Link to="/" className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <header className="bg-[#111] border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-white font-outfit font-bold text-lg">{bookInfo?.book_title}</h1>
            <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">
              <BookOpen size={12} />
              Free Document
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition-colors"
          >
            <Download size={14} />
            Download PDF
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Page Image */}
        <div className="relative w-full max-w-3xl bg-white shadow-2xl" style={{ minHeight: '70vh' }}>
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
              <Loader2 size={32} className="animate-spin text-green-500" />
            </div>
          )}
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
              <div className="text-center">
                <AlertCircle size={32} className="text-red-500 mx-auto mb-2" />
                <p className="text-zinc-600 text-sm">Failed to load page</p>
                <button
                  onClick={() => { setImageError(false); setImageLoading(true); }}
                  className="mt-2 text-green-500 text-sm hover:underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
          <img
            key={currentPage}
            src={imageUrl}
            alt={`Page ${currentPage}`}
            className="w-full h-auto"
            onLoad={() => setImageLoading(false)}
            onError={() => { setImageLoading(false); setImageError(true); }}
            style={{ display: imageLoading || imageError ? 'none' : 'block' }}
            draggable="false"
          />
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-6 mt-6">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-3 bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="flex items-center gap-3 text-white">
            <span className="text-zinc-400">Page</span>
            <input
              type="number"
              value={currentPage}
              onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
              className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 text-center text-white"
              min={1}
              max={pageCount}
            />
            <span className="text-zinc-400">of {pageCount}</span>
          </div>
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= pageCount}
            className="p-3 bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}
